import isEqual from 'lodash/isEqual'
import isFunction from 'lodash/isFunction'
import isPlainObject from 'lodash/fp/isPlainObject'
import { useRouter } from 'next/router'
import qs from 'qs'
import { formatFiltersApiDate, formatFiltersApiTime } from '~/common/dateUtils'
import { isClient, isNextJsPage, isServer } from '~/common/nextJsUtils'
import { FilterType } from './types'

/**
 * Analyzes the provided filter's shape to determine its type.
 *
 * Possible types: AsyncList, List, BusinessUnit, DateRange, Select, Unknown
 */
export const getFilterType = ({ type, optionsFromApi }) => {
  switch (type) {
    case 'list':
      return optionsFromApi ? FilterType.AsyncList : FilterType.List

    case 'bu':
      return FilterType.BusinessUnit

    case 'date_range':
      return FilterType.DateRange

    case 'time_range':
      return FilterType.TimeRange

    case 'select':
      return FilterType.Select

    default:
      return FilterType.Unknown
  }
}

/**
 * Creates an empty initial value for a filter based on its type.
 */
const emptyInitializer = filter => {
  const filterType = getFilterType(filter)

  switch (filterType) {
    case FilterType.AsyncList:
    case FilterType.List:
    case FilterType.Select:
    case FilterType.BusinessUnit:
      return []

    case FilterType.DateRange:
    case FilterType.TimeRange:
      return { from: null, to: null }

    default:
      return ''
  }
}

/**
 * Provides an initial value for a filter based on its type. Selects the
 * initial value from either the current URL's search params or a default
 * value.
 */
const createUrlInitializer = filters => {
  const urlParams = new URLSearchParams(isServer() ? '' : window.location.search)

  // See if all the filters have no url param, but it's ok if there are other
  // unrelated query params, we don't want to take those into consideration.
  const allFilterIds = filters.map(f => f.id)
  const urlParamsIsEmpty = Array.from(urlParams.keys()).filter(key => allFilterIds.includes(key)).length === 0

  return filter => {
    const filterType = getFilterType(filter)
    const { id } = filter

    // if a value isn't found in the url params, we'll use its empty value
    // instead
    const emptyValue = emptyInitializer(filter)

    if (urlParamsIsEmpty) {
      return filter.defaultValue ?? emptyValue
    }

    // optional parsing function to convert url params into one that will match filter options
    if (isFunction(filter.queryParser)) {
      return filter.queryParser(urlParams.get('id'))
    }

    switch (filterType) {
      case FilterType.AsyncList:
      case FilterType.List:
      case FilterType.Select:
      case FilterType.BusinessUnit: {
        let value = urlParams.get(id)?.split(',') ?? emptyValue
        if (filter.listType === 'number') value = value.map(v => parseInt(v, 10))
        if (filter.options?.length) {
          const optionValues = new Set(filter.options.map(o => String(o?.value)))
          value = value.filter(v => optionValues.has(String(v)))
        }
        return value
      }

      case FilterType.DateRange: {
        const from = urlParams.get(`${id}_from`)
        const to = urlParams.get(`${id}_to`)

        return {
          from: from ? new Date(from) : null,
          to: to ? new Date(to) : null
        }
      }

      default:
        return urlParams.get(id) ?? emptyValue
    }
  }
}

/**
 * Returns a filter value initializer.
 *
 * If using `nextjs`, returns a `serverInitializer` that provides default empty
 * values for all filters. Otherwise we're in the browser browser, so we
 * initialize a `queryInitializer` that provides either a value from the
 * current URL query string or default empty values.
 *
 * @param {boolean} useEmptyValues Flag for forcing the use of the empty values
 * initializer.
 */
const getInitializer = (filters, useEmptyValues) => {
  if (useEmptyValues) {
    return emptyInitializer
  }

  // call the query initializer first so it uses the current URL's query string
  return createUrlInitializer(filters)
}

/**
 * Returns an object of initial values for each filter.
 *
 * Initial values are determined based on whether we're in `nextjs` or not. If
 * we're not in `nextjs`, we attempt to load values from the URL params. If a
 * value isn't found or we aren't in `nextjs`, we'll use an empty default
 * value.
 */
export const initializeFilterValues = (filters, { useEmptyValues = false } = {}) => {
  // get the current initializer
  const initializer = getInitializer(filters, useEmptyValues)

  // reduce the filters into a single object
  return filters.reduce((acc, filter) => {
    // get a filter's initial value using the initializer
    const value = initializer(filter)

    // set the filter on the accumulator and return it to move on to the next
    // filter
    acc[filter.id] = value
    return acc
  }, {})
}

/**
 * Transforms a filter's value into its corresponding url params shape.
 */
export const transformFilterValues = (filter, value) => {
  if (value == null || value === '') return undefined

  const filterType = getFilterType(filter)

  switch (filterType) {
    // By default, merge the lists into a comma-separated string, e.g. ['1', '2', '3']
    // becomes '1,2,3'
    // If the filter specifies `joinValuesAsString: false`, keep the value as-is as an array
    // (provided it is non-empty)
    case FilterType.AsyncList:
    case FilterType.List:
    case FilterType.Select:
    case FilterType.BusinessUnit: {
      if (!value.length) {
        return undefined
      }
      if (filter.joinValuesAsString === false) {
        return value
      }
      return value.join()
    }

    // convert date range into a {to, from} depending on whether to and from
    // exist
    case FilterType.DateRange: {
      return {
        from: value.from ? formatFiltersApiDate(value.from) : undefined,
        to: value.to ? formatFiltersApiDate(value.to) : undefined
      }
    }

    case FilterType.TimeRange: {
      return {
        from: value.from ? formatFiltersApiTime(value.from) : undefined,
        to: value.to ? formatFiltersApiTime(value.to) : undefined
      }
    }
    default:
      return value
  }
}

/**
 * Transforms the value for each filter into the expected url params shape.
 */
export const transformFilters = (filters, values, keepNullValues = false) =>
  filters.reduce((transformed, filter) => {
    const value = values[filter.id]
    const transformedValue = transformFilterValues(filter, value)

    // if we get back no value, continue on to the next filter
    if (transformedValue == null) {
      return keepNullValues ? { ...transformed, [filter.id]: undefined } : transformed
    }
    // If it's an array, add each entry as a separate URL query parameter
    if (Array.isArray(transformedValue)) {
      const arrayValues = transformedValue.reduce(
        (acc, value, index) => ({ ...acc, [`${filter.id}[${index}]`]: value }),
        // If the filter is being merged into a parsed URL, it's possible that the indexed array got flattened
        // into a single parameter, so this makes sure we override that one with the new value(s)
        { [filter.id]: undefined }
      )
      return { ...transformed, ...arrayValues }
    }
    // if it's a plain object, prepend each key with the id of the current
    // filter and add it to the transformed object
    if (isPlainObject(transformedValue)) {
      const prependedValues = Object.entries(transformedValue).reduce(
        (values, [key, value]) => ({
          ...values,
          [`${filter.id}_${key}`]: value
        }),
        {}
      )
      return { ...transformed, ...prependedValues }
    }

    // if it's any other value, we'll set the value of the filter id to that
    // value
    return { ...transformed, [filter.id]: transformedValue }
  }, {})

const useMaybeRouter = isNextJsPage() ? useRouter : () => ({})

/**
 * @deprecated When used with NextJs server side rendering, can cause undesirable result like params being removed.
 * Use useSyncToUrlState instead.
 */
export const useUpdateUrl = () => {
  const router = useMaybeRouter()
  return params => {
    // Early return if the params already match the ones in the url
    const locationSearch = isClient() ? qs.parse(window.location.search.substr(1)) : {}
    if (isEqual(locationSearch, params)) return

    const encodedURL = qs.stringify(params)

    // Use either Next.js router or the native window.history
    if (isNextJsPage() && isFunction(router.push)) {
      router.push(`?${encodedURL}`, undefined, { shallow: true })
    } else {
      window.history.pushState(params, document.title, `?${encodedURL}`)
    }
  }
}

/**
 * Get the count of values in the filter object
 */
export const countFilterValues = filters => {
  let length = 0
  Object.keys(filters).forEach(key => {
    if (typeof filters[key] === 'boolean') length++
    if (filters[key] instanceof Array) length += Object.values(filters[key]).length
    if (isPlainObject(filters[key])) {
      Object.keys(filters[key]).forEach(k => {
        if (filters[key][k] instanceof Date) length++
      })
    }
  })
  return length
}
